import type { Cable } from '@anycable/core'
import { Channel } from '@anycable/web'
import React, { createContext, useContext, useEffect, useState } from 'react'

import { useCable } from '../CableProvider'
import type { MessageActionData } from './@types'

const buildSubscription = (cable: Cable) => {
  const channel = new Channel<{ channel: string }, MessageActionData>({
    channel: 'NotificationsChannel',
  })

  const subscription = cable.subscribe(channel)

  subscription.on('message', data => {
    channel.perform('ack', { uuid: data.uuid })
  })

  return subscription
}

type Subscription = ReturnType<typeof buildSubscription>

type ContextState =
  | {
      loaded: true
      subscription: Subscription
    }
  | {
      loaded: false
      subscription: undefined
    }

const StoreContext = createContext<ContextState | undefined>(undefined)

function CableSubscriptionProvider({ children }: React.PropsWithChildren) {
  const { loaded, cable } = useCable()
  const [subscription, setSubscription] = useState<Subscription>()

  useEffect(() => {
    if (loaded) {
      const subscription = buildSubscription(cable)
      setSubscription(subscription)
    }
  }, [loaded])

  return (
    <StoreContext.Provider
      value={
        cable && subscription
          ? {
              loaded: true,
              subscription,
            }
          : {
              loaded: false,
              subscription: undefined,
            }
      }
    >
      {children}
    </StoreContext.Provider>
  )
}
const useCableSubscription = () => {
  const context = useContext(StoreContext)
  if (!context) {
    throw new Error('useCableSubscription must be used within a CableSubscriptionProvider')
  }
  return context
}

export { useCableSubscription }
export default CableSubscriptionProvider
